import type { OnlineOrder } from '@restaurant/api-client';
import type { Payment } from '@restaurant/api-contracts';
import { onlineProviderShortName } from '@restaurant/types';

type PaymentMethodKind = NonNullable<OnlineOrder['paymentMethodKind']>;

/**
 * Whether a payment can be refunded in-app: a captured card (ONLINE) or bank
 * transfer (MANUAL) that is PAID / PARTIALLY_REFUNDED with money still owed back.
 * Cash / card-on-handoff / unpaid / fully-refunded payments are not refundable.
 * Shared by the cancel sheet's refund toggle and the cancelled-order "Refund now"
 * action so the two surfaces can never disagree on what's refundable.
 */
export function isPaymentRefundable(payment: Payment | undefined): boolean {
  if (!payment) return false;
  const methodOk = payment.methodKind === 'ONLINE' || payment.methodKind === 'MANUAL';
  const statusOk = payment.status === 'PAID' || payment.status === 'PARTIALLY_REFUNDED';
  return methodOk && statusOk && payment.amountCents - payment.refundedCents > 0;
}

// Framework7 glyph paired with each rail - cash vs card vs bank, so the row reads
// at a glance. All names are standard f7-icons (see BillPage for precedent).
const METHOD_ICONS: Record<PaymentMethodKind, string> = {
  CASH: 'money_dollar_circle',
  CARD_ON_HANDOFF: 'creditcard',
  MANUAL: 'building_2_fill',
  ONLINE: 'creditcard',
  GIFT_CARD: 'gift',
};

/**
 * Operator-facing name for a payment rail. `labels` is the current-locale
 * `t.orders.methodKind` map (passed in - this is a pure util, not a hook) so the
 * wording is the single source of truth across the list row + detail inspector.
 *
 * `methodId` names WHICH instance of the rail took the money. On ONLINE that is
 * the gateway, and its brand wins over the generic "Online": a restaurant can
 * run Menucat and PayPal at the same time, and an operator chasing a payment
 * needs to know which one to open. Every other rail falls back to the kind.
 */
export function paymentMethodLabel(
  kind: PaymentMethodKind,
  labels: Record<PaymentMethodKind, string>,
  methodId?: string | null,
): string {
  return onlineProviderShortName(methodId) ?? labels[kind] ?? kind;
}

export function paymentMethodIcon(kind: PaymentMethodKind): string {
  return METHOD_ICONS[kind] ?? 'creditcard';
}
